import { ownAccessToken } from '@/services/accessToken';
import { makeObservable, observable, computed, action, runInAction, reaction } from 'mobx';
import { loadDeviceSigningKey } from '@/services/deviceSigningKeySlot';
import { markSignedIn, markSignedOut } from '@/services/presenceCookie';
import { leaveForThirdParty } from '@/desktop/leaveForThirdParty';
import type {
  IAuthResponse,
  IDeploymentSetupStatus,
  IOAuthIdentityLink,
  IPasswordResetReview,
  IReauthGranted,
  IRecentlyAddedSignInMethod,
  IUser,
  OAuthProviderType,
} from '@featit/shared';
import { TERMS_VERSION } from '@featit/shared/legal';
import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/browser';
import {
  authApi,
  type IClaimPeek,
  type ICompleteSetupInput,
  type IContactAddressStanding,
  type IPasskey,
} from '@/services/AuthApi';
import {
  SESSION_END_CAUSES,
  endSession,
  onSessionEnd,
  decodeStoredCredentialKind,
  hasStoredAuthSession,
  isHttpAuthError,
  refreshAccessToken,
  storeAuthTokens,
  REFRESH_KEY,
} from '@/services/ApiService';
import { SESSION_END_PATHS, type SessionEndPathType } from '@/services/sessionEnd';
import { getAsIfState } from '@/features/as-if/asIfView';
import { disposeRealtimeClient } from '@/services/realtimeSocket';
import { pairingApi } from '@/services/PairingApi';
import { endServerSession } from '@/services/signOut';
import { holdsCurrentRefreshSecret } from '@/services/refreshSecretWriteAhead';
import {
  isDeploymentUnreachableError,
  sessionVerificationFailure,
  signInFailureMessage,
} from '@/features/onboarding/signInFailure';
import { rememberPasskeyUsedHere } from '@/features/auth/passkeyUsedHere';
import {
  expiredTokenMessage,
  passwordResetRateLimitSentence,
  failureToSay,
  type HeldSentenceType,
  mailRefusalMessage,
  pageWords,
  ReaderFacingError,
  sayFailure,
  setupRefusalMessage,
} from '@/services/actionFailure';
import { forgetShellPushDevice } from '@/shell/pushRegistration';
import { forgetPendingDirectEntries } from '@/features/machines/machineDirectPending';
import {
  forgetPendingInvitation,
  rememberSignUpInvitation,
} from '@/features/access/pendingInvitation';
import { BOOT_MARKS, markBoot } from '@/shell/bootMarks';
import { ceremoniesRunInTheSheet, loadPasskeySheet } from '@/desktop/passkeySheetPlace';
import { effectiveDeploymentAddress } from '@/features/onboarding/deploymentAddress';
import { passkeyAssertion } from '@/features/auth/passkeyAssertion';
import {
  PasskeyAtAnotherAddressError,
  assertCeremonyRunsHere,
  goToCeremonyOrigin,
} from '@/features/auth/passkeyOrigin';
import { passkeyAtOtherTailnetName } from '@/features/auth/passkeyTailnetName';
import { refusalDestinationOfError } from '@/services/refusalDestinationOfError';
import type { RefusalDestinationIdType } from '@featit/shared';
import { followTermsRefusals } from '@/features/legal/termsRefusal';
import type { MessageKey } from '@featit/shared/i18n/messages/index';
import { adoptSessionLanguage } from '@/i18n/sessionLanguage';

/**
 * T-0559: the browser's passkey ceremonies, loaded when a passkey is first used. Every launch parses
 * the entry, and only a sign-in or a passkey setup runs a ceremony.
 */
/**
 * I-1879: a sign-in is done once its refresh secret is stored, not when the server answered.
 *
 * The secret is wrapped by the vault before it reaches storage (I-1625), which takes a moment: the
 * key is opened from IndexedDB, or made the first time. Signed in before that, the page went on —
 * and a navigation in that gap (a reload, a link, the next `page.goto`) unloaded the page with the
 * write still queued. The access token was stored; the secret never was. The next renewal had
 * nothing to present and ended a session the server still held. ADR-100's I-1625 residual called
 * the gap "a few milliseconds"; measured on dev (eed5d8c20) the slot was still empty once the page
 * had left `/login` and loaded, and was written within 3 s.
 *
 * ⛔ Awaited, never `void`: the one store path the write-ahead refresh uses too (`storeAuthTokens`,
 * I-1623), so a sign-in and a renewal store their secret the same way. `writeCurrentRefreshSecret`
 * never rejects (with no vault it keeps the secret in memory), so this only waits.
 */
function storeSignIn(tokens: { accessToken: string; refreshToken?: string }): Promise<void> {
  return storeAuthTokens(tokens);
}

function webAuthnBrowser(): Promise<typeof import('@simplewebauthn/browser')> {
  return import('@simplewebauthn/browser');
}

/**
 * A response this store is about to store as a list, made safe to store as one (I-0582, AC-5).
 *
 * Every call below that assigns a server response straight to an observable list is read this
 * way, so a response whose shape has drifted — an older deployment, a proxy that unwraps a body, a
 * test fixture returning something else — degrades to "nothing yet" instead of handing a
 * non-array to a component that will call `.map`/`.filter`/`.includes` on it and take the whole
 * route down. Never widen the field's type to admit `undefined` instead: that only moves the crash
 * to whichever reader is next.
 */
function asList<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

/** F-0160 AC-25b — asking for a new confirmation link from the sign-in screen. */
export type VerificationResendStateType =
  | 'idle'
  | 'sending'
  | 'sent'
  | 'already-verified'
  | 'failed';

/**
 * F-0160 AC-25: the sign-in door's answer for a right password on an address that was never
 * confirmed — a 403 carrying `code: 'email_unverified'`. Read by the code, never by the sentence.
 */
export function isEmailUnverifiedRefusal(error: unknown): boolean {
  const response = (error as { response?: { status?: unknown; data?: { code?: unknown } } } | null)
    ?.response;
  return response?.status === 403 && response.data?.code === 'email_unverified';
}

/** F-0581 OB-15: where the setup read stands — not asked, on its way, answered, or no answer. */
export type SetupStatusReachType = 'idle' | 'pending' | 'answered' | 'unreachable';

export class AuthStore {
  user: IUser | null = null;
  passkeys: IPasskey[] = [];
  /** The providers this deployment offers. Empty until asked, and empty forever if none are configured. */
  oauthProviders: OAuthProviderType[] = [];
  /** What this person has connected. Loaded on the profile screen. */
  oauthIdentities: IOAuthIdentityLink[] = [];
  loading = false;
  passkeyLoading = false;
  oauthLoading: OAuthProviderType | null = null;
  /**
   * ADR-160 AC-18 (I-1078): the provider whose sign-in or link the desktop app handed to the system
   * browser. The page is never replaced there, so `oauthLoading` is cleared and this says where the
   * flow went instead; the `featit-return` link brings the result back to the same screen.
   */
  oauthInBrowser: OAuthProviderType | null = null;
  /** F-0819: the confirm page went to the system browser, and this window waits for its return. */
  browserSignInOpen = false;
  initialized = false;
  /** T-0471: what `error` says, kept, so it is written in the page's language when read. */
  heldError: HeldSentenceType | null = null;
  get error(): string | null {
    return sayFailure(pageWords(), this.heldError);
  }
  set error(value: HeldSentenceType | null) {
    this.heldError = value;
    this.signInUnreachable = false;
  }
  /** T-1144: the held error says a password or passkey sign-in never got an answer. */
  signInUnreachable = false;
  /**
   * T-0824: the last refusal `authErrorMessage` worded, with the place it named. It answers
   * `errorDestination` only while `error` is still that text, so every reset of `error` drops it.
   */
  lastRefusal: { text: HeldSentenceType; destination: RefusalDestinationIdType | null } | null =
    null;
  /** I-2150: kept as what failed, so the waiting screen writes it in the reader's language. */
  sessionVerificationError: HeldSentenceType | null = null;
  /**
   * F-0160 AC-25a: the address a sign-up just sent its confirmation link to. Set by `register`, which
   * signs nobody in any more; `RegisterPage` shows "check your email" while it is set.
   */
  pendingVerificationEmail: string | null = null;
  /**
   * F-0160 AC-25: the address whose password was right and whose confirmation is still outstanding.
   * Set by `login` on a `403 email_unverified`, which is **not** an error — `LoginPage` offers a new
   * link instead of a red box.
   */
  unverifiedSignInEmail: string | null = null;
  /** I-2229: the address the last confirmation link confirmed, so the sign-in after it is filled in. */
  confirmedEmail: string | null = null;
  /** F-0160 AC-25b: where asking for a new link without a session stands. */
  verificationResend: VerificationResendStateType = 'idle';
  /** T-0471: what `verificationResendError` says, kept, so it is written in the page's language when read. */
  heldVerificationResendError: HeldSentenceType | null = null;
  get verificationResendError(): string | null {
    return sayFailure(pageWords(), this.heldVerificationResendError);
  }
  set verificationResendError(value: HeldSentenceType | null) {
    this.heldVerificationResendError = value;
  }
  /**
   * F-0160 §11 (I-0638): whether this deployment is waiting for its first administrator. `null`
   * until asked, and after a failed ask — the sign-in and sign-up screens then show their ordinary
   * forms rather than guessing.
   */
  setupRequired: boolean | null = null;
  /**
   * F-0160 §11.5 (I-0638): whether the browser door is open, and until when. `null` until asked,
   * like `setupRequired`.
   */
  firstRun: IDeploymentSetupStatus['firstRun'] | null = null;
  /**
   * F-0581 OB-15: whether the server answered the setup read, which Get started's brand bar shows.
   * Any answer counts, a refusal included; only a transport failure is `unreachable`.
   */
  setupStatusReach: SetupStatusReachType = 'idle';

  private readonly beforeSignOut = new Set<() => void>();

  constructor() {
    makeObservable(this, {
      user: observable,
      passkeys: observable,
      oauthProviders: observable,
      oauthIdentities: observable,
      loading: observable,
      passkeyLoading: observable,
      oauthLoading: observable,
      oauthInBrowser: observable,
      initialized: observable,
      heldError: observable.ref,
      browserSignInOpen: observable,
      beginBrowserSignIn: action,
      dismissBrowserSignIn: action,
      signInUnreachable: observable,
      error: computed,
      lastRefusal: observable.ref,
      errorDestination: computed,
      sessionVerificationError: observable,
      pendingVerificationEmail: observable,
      unverifiedSignInEmail: observable,
      confirmedEmail: observable,
      verificationResend: observable,
      heldVerificationResendError: observable.ref,
      verificationResendError: computed,
      setupRequired: observable,
      firstRun: observable,
      setupStatusReach: observable,
      isAuthenticated: computed,
      hasRecoverableSession: computed,
      isVerificationSession: computed,
      login: action,
      loginWithPasskey: action,
      register: action,
      logout: action,
      sessionEndedElsewhere: action,
      initialize: action,
      retrySessionVerification: action,
      loadPasskeys: action,
      registerPasskey: action,
      deletePasskey: action,
      loadOAuthProviders: action,
      beginOAuthSignIn: action,
      completeOAuthSignIn: action,
      completeVerificationRedemption: action,
      loadOAuthIdentities: action,
      beginOAuthLink: action,
      disconnectOAuthProvider: action,
      dismissOAuthInBrowser: action,
      setContactEmail: action,
      acceptTerms: action,
      clearError: action,
      confirmEmailVerification: action,
      resendEmailVerification: action,
      resendVerificationForSignIn: action,
      clearPendingVerification: action,
      requestPasswordReset: action,
      confirmPasswordReset: action,
      loadSetupStatus: action,
      completeSetup: action,
      completeFirstRun: action,
      peekClaim: action,
      claimAccount: action,
    });
    // F-0534 PPD-3 (I-1311): every session end arrives here, from `endSession`, and the person is
    // let go at once — also for a refresh the server refused mid-page, which used to leave them
    // set until the sign-in page had loaded. Another person taking over is already the new `user`.
    onSessionEnd((cause) => {
      if (cause === SESSION_END_CAUSES.ANOTHER_PERSON) return;
      runInAction(() => this.dropSignedInState());
    });
    // F-0489 (§3): whoever signs in gets this page's device signing key, made with no prompt and
    // registered in the background, so their first answer is signed without waiting for it.
    reaction(
      () => this.user?.id ?? null,
      (userId) => {
        if (!userId) return;
        // The module loads here, off the launch entry; a sign-out before it arrives makes no key.
        void loadDeviceSigningKey()
          .then((keys) => (this.user?.id === userId ? keys.prepareDeviceSigningKey(userId) : null))
          .catch(() => undefined);
      },
    );
    // I-1971: the person's language comes with their session, and is adopted in the same batch that sets
    // the user, so no screen is drawn for them in the browser's language first.
    reaction(
      () => this.user?.localePreferences,
      (preferences) => adoptSessionLanguage(preferences),
    );
    // I-2273 (AC-TA19): a call refused for the terms asks for the own account again, so the terms
    // screen opens in place of the app. Nobody is signed out.
    followTermsRefusals(() => this.reloadOwnAccount());
  }

  /** I-2273 (AC-TA19): the own account read again, as the server has it now. */
  async reloadOwnAccount(): Promise<void> {
    if (!this.user) return;
    const account = await authApi.me();
    runInAction(() => {
      if (this.user?.id === account.id) this.user = account;
    });
  }

  get isAuthenticated(): boolean {
    return !!this.user && !!ownAccessToken();
  }

  get hasRecoverableSession(): boolean {
    return !this.user && hasStoredAuthSession();
  }

  /**
   * Whether the signed-in credential is a verification session (I-0353, AC-U) — read-anything,
   * write-nothing by ceiling (concept §8.2). A write control gates on this to disable itself
   * *before* the request that would refuse it, rather than firing a request the server was always
   * going to reject.
   *
   * Depends on `this.user` rather than reading the token unconditionally, so it recomputes on
   * every login/logout/redemption transition and never answers about a token an earlier session
   * left behind.
   */
  get isVerificationSession(): boolean {
    return Boolean(this.user) && decodeStoredCredentialKind() === 'verification';
  }

  /** The session check under way, so a second caller waits on it rather than asking again (T-0559). */
  private pendingInitialize: Promise<void> | null = null;

  initialize({ force = false }: { force?: boolean } = {}): Promise<void> {
    if (this.initialized && !force) return Promise.resolve();
    if (this.pendingInitialize && !force) return this.pendingInitialize;
    const check = this.verifyStoredSession();
    this.pendingInitialize = check;
    void check.finally(() => {
      if (this.pendingInitialize === check) this.pendingInitialize = null;
    });
    return check;
  }

  private async verifyStoredSession(): Promise<void> {
    if (!hasStoredAuthSession()) {
      // ADR-133: nothing is signed in here, so a presence cookie left behind would only cost the
      // next visit to the root a hop through the app to sign-in.
      markSignedOut();
      runInAction(() => {
        this.user = null;
        this.initialized = true;
        this.sessionVerificationError = null;
      });
      return;
    }

    try {
      // T-0559: once per document, so a launch can say when its session check went and came back.
      markBoot(BOOT_MARKS.sessionSent);
      const user = await this.fetchCurrentUserWithRefresh();
      markBoot(BOOT_MARKS.sessionAnswered);
      // ADR-133: a session from before the presence cookie existed, or one whose cookie was cleared
      // by the browser, gets it back the moment it is proven — or its root would show the website.
      markSignedIn();
      runInAction(() => {
        this.user = user;
        this.initialized = true;
        this.sessionVerificationError = null;
      });
    } catch (err: unknown) {
      const shouldClearSession = isHttpAuthError(err);
      if (shouldClearSession) {
        endSession(SESSION_END_CAUSES.SERVER);
      }
      runInAction(() => {
        this.user = null;
        this.initialized = true;
        this.sessionVerificationError = shouldClearSession ? null : sessionVerificationFailure(err);
      });
    }
  }

  /** I-2181: `invitation`, a link sent to an address, signs in as that address (`email` empty). */
  async login(email: string, password: string, invitation?: string | null) {
    this.loading = true;
    this.error = null;
    this.unverifiedSignInEmail = null;
    this.verificationResend = 'idle';
    this.verificationResendError = null;
    try {
      const res = await authApi.login(email, password, invitation);
      await storeSignIn(res);
      runInAction(() => {
        this.user = res.user;
        this.loading = false;
        this.sessionVerificationError = null;
      });
    } catch (err: unknown) {
      runInAction(() => {
        // I-2181: a sign-in fixed to an invitation's address has no typed address to resend to, so
        // the refusal's own words say what to do.
        if (isEmailUnverifiedRefusal(err) && email.trim()) {
          // F-0160 AC-25: the password was right. Nothing is wrong with what they typed, so this is
          // a next step on the sign-in screen, not an error.
          this.unverifiedSignInEmail = email.trim();
        } else {
          // A deployment that never answered has no opinion about these credentials, and must not
          // be reported as if it had rejected them.
          this.error = signInFailureMessage(err, 'auth.actions.signIn');
          this.signInUnreachable = isDeploymentUnreachableError(err);
        }
        this.loading = false;
      });
    }
  }

  /**
   * PX-14 (ADR-191): the session a customer's one-tap entry answered, stored as a sign-in is, so
   * they stay signed in on this phone and the session renews like any other.
   */
  async adoptSignIn(res: IAuthResponse): Promise<void> {
    await storeSignIn(res);
    runInAction(() => {
      this.user = res.user;
      this.error = null;
      this.sessionVerificationError = null;
    });
  }

  async loginWithPasskey(email?: string) {
    this.passkeyLoading = true;
    this.error = null;
    try {
      await this.assertWebAuthnUsable();
      // F-0489 slice 3: the ceremony binds a device key made for it. A page that cannot make one
      // signs in as before, and registers its key after.
      const bindingKey = await loadDeviceSigningKey()
        .then((keys) => keys.deviceKeyForPasskeySignIn())
        .catch(() => null);
      const challenge = await authApi.beginPasskeyAuthentication(
        email?.trim() || undefined,
        bindingKey?.jwk,
      );
      const response = await this.passkeyAssertion(challenge.options, challenge.origin);
      const res = await authApi.verifyPasskeyAuthentication({
        challengeId: challenge.challengeId,
        response,
        ...(bindingKey
          ? {
              deviceKeyBinding: {
                jwk: bindingKey.jwk,
                client: bindingKey.client,
                signature: await bindingKey.prove(challenge.options.challenge),
              },
            }
          : {}),
      });
      if (bindingKey && res.deviceSigningKey?.thumbprint === bindingKey.thumbprint) {
        bindingKey.keep(res.user.id, res.deviceSigningKey.deploymentId);
      } else {
        // T-0837: an unbound Mac app key is deleted from the Keychain.
        bindingKey?.drop();
      }
      await storeSignIn(res);
      rememberPasskeyUsedHere();
      runInAction(() => {
        this.user = res.user;
        this.passkeyLoading = false;
        this.sessionVerificationError = null;
      });
    } catch (err: unknown) {
      if (err instanceof PasskeyAtAnotherAddressError) {
        // I-1417: signing in needs no session from this address, so the page goes to where the
        // passkey is used and the person signs in there. Still loading: the page is leaving.
        // F-0821: unless that address is this Mac's other tailnet name, which is switched off with the
        // active Tailscale login: say so instead of leaving for a page that does not answer.
        const tailnetSentence = await passkeyAtOtherTailnetName(err.ceremonyOrigin);
        if (tailnetSentence) {
          runInAction(() => {
            this.error = tailnetSentence;
            this.signInUnreachable = false;
            this.passkeyLoading = false;
          });
          return;
        }
        goToCeremonyOrigin(err);
        return;
      }
      runInAction(() => {
        this.error = this.passkeyErrorMessage(err, 'auth.actions.signInPasskey');
        // Only a request that got no answer: a browser's own passkey error carries a `code` too.
        this.signInUnreachable =
          (err as { isAxiosError?: boolean } | null)?.isAxiosError === true &&
          isDeploymentUnreachableError(err);
        this.passkeyLoading = false;
      });
    }
  }

  /**
   * F-0332, ADR-151 §5: the signed-in person proves they are present with their passkey, on the same
   * ceremony signing in uses, and receives a five-minute step-up for approving a new device — not a
   * session. Throws a sentence written for the reader when the ceremony or the server refuses.
   */
  async confirmIdentityWithPasskey(): Promise<IReauthGranted> {
    try {
      await this.assertWebAuthnUsable();
      const email = this.user?.email; // actor-name: the signed-in reader's own account, not a contributor
      const challenge = await authApi.beginPasskeyAuthentication(email ?? undefined);
      const response = await this.passkeyAssertion(challenge.options, challenge.origin);
      return await pairingApi.reauthWithPasskey(challenge.challengeId, response);
    } catch (err: unknown) {
      throw new Error(
        sayFailure(pageWords(), this.passkeyErrorMessage(err, 'auth.actions.confirmPasskey')),
      );
    }
  }

  /**
   * F-0160 AC-25a, D-0041 = a: creates the account and signs nobody in. Nothing is stored — the
   * server hands out no tokens until the address is confirmed — and `pendingVerificationEmail` names
   * where the link went.
   */
  async register(
    email: string,
    password: string,
    name: string,
    invitation?: string | null,
    place?: string | null,
    deploymentInvitation?: string | null,
  ) {
    this.loading = true;
    this.error = null;
    this.pendingVerificationEmail = null;
    try {
      const res = deploymentInvitation
        ? await authApi.register(email, password, name, null, null, deploymentInvitation)
        : place
          ? await authApi.register(email, password, name, invitation, place)
          : await authApi.register(email, password, name, invitation);
      // F-0809: the deployment invitation is spent with the account, so this device forgets it.
      if (deploymentInvitation) forgetPendingInvitation();
      // F-0584 MWI-28 (I-2170): the account was made from this link, so the first sign-in says what
      // it joined and opens it.
      if (invitation) rememberSignUpInvitation(invitation, place);
      runInAction(() => {
        this.pendingVerificationEmail = res.email || email.trim();
        this.loading = false;
      });
    } catch (err: unknown) {
      runInAction(() => {
        this.error = signInFailureMessage(err, 'auth.actions.createAccount');
        this.loading = false;
      });
    }
  }

  /**
   * Which providers this deployment offers.
   *
   * ⚠️ A failure here is deliberately silent. The sign-in screen still has a password form and a
   * passkey button, and an error box about a capability nobody asked for is noise on the one screen
   * where the person is already trying to get somewhere.
   *
   * ⛔ A response that **succeeds** with a shape other than `{ providers: [...] }` is treated the
   * same as a request that throws: "no providers configured" (I-0582). A `catch` only ever sees a
   * request that fails to complete — an older deployment mid-rollover, a proxy that unwraps the
   * body, a test fixture returning the bare list — and none of those are network failures. Widening
   * `oauthProviders` to admit `undefined` would only move the crash to whichever component reads it
   * next (`providerButtonsFor` calls `.includes` on it); this is the one place that turns an
   * unexpected shape into a known one, so nothing downstream has to guard against it again.
   */
  async loadOAuthProviders() {
    try {
      const response = await authApi.oauthProviders();
      runInAction(() => {
        this.oauthProviders = asList<OAuthProviderType>(response?.providers);
      });
    } catch {
      runInAction(() => {
        this.oauthProviders = [];
      });
    }
  }

  /**
   * Leave for the provider.
   *
   * In a browser tab this navigates away on success, so there is no "finished" state to reset — the
   * loading flag stays set until the page is replaced, which is what stops a second click starting a
   * second flow. In the desktop app the provider opens in the system browser and this page stays
   * (ADR-160 AC-18), so the flag is cleared and `oauthInBrowser` names where the flow went.
   */
  async beginOAuthSignIn(provider: OAuthProviderType, redirect?: string) {
    this.oauthLoading = provider;
    this.oauthInBrowser = null;
    this.error = null;
    try {
      const { authorizationUrl } = await authApi.startOAuthSignIn(provider, redirect);
      await this.leaveForProvider(provider, authorizationUrl);
    } catch (err: unknown) {
      runInAction(() => {
        this.error = signInFailureMessage(err, 'auth.actions.startSignIn');
        this.oauthLoading = null;
      });
    }
  }

  /**
   * Finish a provider sign-in by swapping the one-time code from the redirect for the credential.
   *
   * Returns whether it worked, because the caller has a URL to tidy up and somewhere to navigate to
   * either way. ⛔ The code is single-use on the server, so this must not be called twice for one
   * code — `LoginPage` strips it from the URL before awaiting. `desktopChallenge` is the one the
   * desktop return link named (I-1437, ADR-160 AC-31); a browser tab has none.
   */
  async completeOAuthSignIn(code: string, desktopChallenge?: string | null): Promise<boolean> {
    this.loading = true;
    this.oauthInBrowser = null;
    this.browserSignInOpen = false;
    this.error = null;
    try {
      const res = await authApi.exchangeOAuthCode(code, desktopChallenge);
      await storeSignIn(res);
      runInAction(() => {
        this.user = res.user;
        this.loading = false;
        this.sessionVerificationError = null;
      });
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        this.error = signInFailureMessage(err, 'auth.actions.completeSignIn');
        this.loading = false;
      });
      return false;
    }
  }

  /**
   * Finish a verification redemption (I-0326, concept §3.3 step 3-4). Same shape as
   * {@link completeOAuthSignIn} for the same reason: the code is single-use, so this must not be
   * called twice for one code — `VerifySessionPage` strips it from the URL before awaiting.
   *
   * Returns `workAreas` alongside success (I-0353, AC-S) so the caller can navigate straight
   * there, in this same tick — not a follow-up call whose result could lose a race to the rest of
   * the app's own post-sign-in effects picking the ordinary default work area first.
   */
  async completeVerificationRedemption(
    code: string,
  ): Promise<{ ok: boolean; workAreas: Array<{ id: string; slug: string; name: string }> }> {
    this.loading = true;
    this.error = null;
    try {
      const res = await authApi.redeemVerificationCode(code);
      await storeSignIn(res);
      runInAction(() => {
        this.user = res.user;
        this.loading = false;
        this.sessionVerificationError = null;
      });
      return { ok: true, workAreas: res.workAreas };
    } catch (err: unknown) {
      runInAction(() => {
        this.error = signInFailureMessage(err, 'auth.actions.redeemLink');
        this.loading = false;
      });
      return { ok: false, workAreas: [] };
    }
  }

  async loadOAuthIdentities() {
    if (!this.user) return;
    try {
      const identities = await authApi.oauthIdentities();
      runInAction(() => {
        this.oauthIdentities = asList<IOAuthIdentityLink>(identities);
      });
    } catch (err: unknown) {
      runInAction(() => {
        this.error = this.authErrorMessage(err, 'auth.actions.loadAccounts');
      });
    }
  }

  /** ⛔ The only way an identity ever joins an existing account: from inside it, while signed in. */
  async beginOAuthLink(provider: OAuthProviderType) {
    this.oauthLoading = provider;
    this.oauthInBrowser = null;
    this.error = null;
    try {
      const { authorizationUrl } = await authApi.startOAuthLink(provider);
      await this.leaveForProvider(provider, authorizationUrl);
    } catch (err: unknown) {
      runInAction(() => {
        this.error = this.authErrorMessage(err, 'auth.actions.connectAccount');
        this.oauthLoading = null;
      });
    }
  }

  /**
   * F-0819: the desktop app asks the browser, where the person may already be signed in, to confirm.
   * It comes back through the same `featit-return` link as a provider sign-in.
   */
  async beginBrowserSignIn() {
    this.browserSignInOpen = false;
    this.error = null;
    try {
      const address = await authApi.desktopBrowserSignInAddress(effectiveDeploymentAddress());
      if ((await leaveForThirdParty(address)) === 'browser') {
        runInAction(() => {
          this.browserSignInOpen = true;
        });
      }
    } catch (err: unknown) {
      runInAction(() => {
        this.error = signInFailureMessage(err, 'auth.actions.startSignIn');
      });
    }
  }

  dismissBrowserSignIn() {
    this.browserSignInOpen = false;
  }

  /** Hides the continue-in-browser notice, so the buttons can start another sign-in. */
  dismissOAuthInBrowser() {
    this.oauthInBrowser = null;
  }

  /** Leaves for the provider; see `beginOAuthSignIn` for what each outcome leaves on screen. */
  private async leaveForProvider(provider: OAuthProviderType, authorizationUrl: string) {
    if ((await leaveForThirdParty(authorizationUrl)) === 'browser') {
      runInAction(() => {
        this.oauthLoading = null;
        this.oauthInBrowser = provider;
      });
    }
  }

  async disconnectOAuthProvider(provider: OAuthProviderType) {
    this.oauthLoading = provider;
    this.error = null;
    try {
      await authApi.unlinkOAuthProvider(provider);
      const identities = await authApi.oauthIdentities();
      runInAction(() => {
        this.oauthIdentities = asList<IOAuthIdentityLink>(identities);
        this.oauthLoading = null;
      });
    } catch (err: unknown) {
      runInAction(() => {
        // The refusal that matters here is "this is your only way to sign in", and the server says
        // it in a sentence somebody can act on. Replacing it with a generic message would throw
        // away the only useful part.
        this.error = this.authErrorMessage(err, 'auth.actions.disconnectAccount');
        this.oauthLoading = null;
      });
    }
  }

  /**
   * F-0750: change the own name. The kept name is folded back into `user`, so every thanks and
   * byline drawn from it reads the new one at once. Answers the kept name, or `null` with `error`
   * set to the sentence to show.
   */
  async setOwnName(name: string): Promise<string | null> {
    this.loading = true;
    this.error = null;
    try {
      const kept = await authApi.setOwnName(name);
      runInAction(() => {
        if (this.user) this.user = { ...this.user, name: kept.name };
        this.loading = false;
      });
      return kept.name;
    } catch (err: unknown) {
      runInAction(() => {
        this.error = this.authErrorMessage(err, 'auth.actions.saveName');
        this.loading = false;
      });
      return null;
    }
  }

  /**
   * Change where FEATit writes to this person, or clear it back to their sign-in address.
   *
   * The response is folded back into `user` so the screen shows what the server stored rather than
   * what was typed — including the delivery standing, which the server resets on every change.
   */
  async setContactEmail(contactEmail: string | null): Promise<IContactAddressStanding | null> {
    this.loading = true;
    this.error = null;
    try {
      const standing = await authApi.setContactEmail(contactEmail);
      runInAction(() => {
        if (this.user) {
          this.user = {
            ...this.user,
            contactEmail: standing.contactEmail,
            contactDeliveryStatus: standing.deliveryStatus,
            needsAnotherContactEmail: standing.needsAnotherContactEmail,
          };
        }
        this.loading = false;
      });
      return standing;
    } catch (err: unknown) {
      runInAction(() => {
        this.error = this.authErrorMessage(err, 'auth.actions.saveAddress');
        this.loading = false;
      });
      return null;
    }
  }

  /**
   * F-0554, AC-TA5: the person accepts the terms in force on the screen that asks before the app
   * works. The answer is the own account, so `termsAcceptanceRequired` turns false and the app
   * opens. Answers the sentence to show on failure, or `null` once recorded.
   */
  async acceptTerms(): Promise<string | null> {
    try {
      const account = await authApi.acceptTerms(TERMS_VERSION);
      runInAction(() => {
        if (this.user) this.user = { ...this.user, ...account };
      });
      return null;
    } catch (err: unknown) {
      return sayFailure(pageWords(), this.authErrorMessage(err, 'auth.actions.acceptTerms'));
    }
  }

  /**
   * I-1287: told first thing at sign-out, while the session still exists — for a write that must
   * leave with the person (a view choice waiting out its debounce). Returns how to stop being told.
   */
  onBeforeSignOut(listener: () => void): () => void {
    this.beforeSignOut.add(listener);
    return () => void this.beforeSignOut.delete(listener);
  }

  /**
   * I-2572 (MS-3): `path` names where the sign-out came from, for the desktop log. The Sign out dialog
   * names itself and is not logged; any other caller is, as `sign-out` when it names nothing.
   */
  logout(path: SessionEndPathType = SESSION_END_PATHS.SIGN_OUT) {
    // I-1287: what must be written as this person goes first, while their session can write it.
    this.beforeSignOut.forEach((listener) => {
      try {
        listener();
      } catch (error) {
        // A listener that fails must not keep the person signed in.
        console.warn('[featit] sign-out: a pending write was not sent', error);
      }
    });
    // F-0234: this phone's push registration goes first, while the session to remove it still exists.
    forgetShellPushDevice();
    // I-0927: the session ends on the server too, before the tokens that name it are cleared.
    endServerSession();
    endSession(SESSION_END_CAUSES.SIGN_OUT, { path });
    // F-0534 PPD-2 (I-1245): an invitation link opened and not yet joined is the signing-out person's.
    forgetPendingInvitation();
    // F-0683 MP-43: so are the narrowings a machine took straight from this device; it posts them anyway.
    forgetPendingDirectEntries();
  }

  /**
   * F-0534 PPD-3: the session ended in another tab or window of this origin — its sign-out, or a
   * refresh the server refused there — and the tokens they share are gone. This one lets go of the
   * person too, rather than showing their pages until its next request fails.
   */
  sessionEndedElsewhere() {
    if (!this.user) return;
    endSession(SESSION_END_CAUSES.ELSEWHERE);
  }

  private dropSignedInState() {
    this.unverifiedSignInEmail = null;
    this.verificationResend = 'idle';
    this.verificationResendError = null;
    // The realtime socket authenticated once, when it connected. Left open it would keep streaming
    // the signed-out user's work areas into the tab, so it goes down with the tokens.
    disposeRealtimeClient();
    this.user = null;
    this.passkeys = [];
    this.oauthIdentities = [];
    this.error = null;
    this.sessionVerificationError = null;
  }

  async retrySessionVerification() {
    this.initialized = false;
    await this.initialize({ force: true });
  }

  async loadPasskeys() {
    if (!this.user) return;
    this.passkeyLoading = true;
    try {
      const passkeys = await authApi.passkeys();
      runInAction(() => {
        this.passkeys = asList<IPasskey>(passkeys);
        this.passkeyLoading = false;
      });
    } catch (err: unknown) {
      runInAction(() => {
        this.error = this.authErrorMessage(err, 'auth.actions.loadPasskeys');
        this.passkeyLoading = false;
      });
    }
  }

  async registerPasskey(name?: string) {
    if (!this.user) return;
    this.passkeyLoading = true;
    this.error = null;
    try {
      await this.assertWebAuthnUsable();
      const challenge = await authApi.beginPasskeyRegistration(name?.trim() || undefined);
      const response = await this.passkeyCredential(challenge.options, challenge.origin);
      const passkey = await authApi.verifyPasskeyRegistration({
        challengeId: challenge.challengeId,
        response,
        name,
      });
      runInAction(() => {
        this.passkeys = [...this.passkeys.filter((item) => item.id !== passkey.id), passkey];
        this.passkeyLoading = false;
      });
    } catch (err: unknown) {
      runInAction(() => {
        this.error = this.passkeyErrorMessage(err, 'auth.actions.addPasskey');
        this.passkeyLoading = false;
      });
    }
  }

  async deletePasskey(id: string) {
    this.passkeyLoading = true;
    this.error = null;
    try {
      await authApi.deletePasskey(id);
      runInAction(() => {
        this.passkeys = this.passkeys.filter((passkey) => passkey.id !== id);
        this.passkeyLoading = false;
      });
    } catch (err: unknown) {
      runInAction(() => {
        this.error = this.authErrorMessage(err, 'auth.actions.deletePasskey');
        this.passkeyLoading = false;
      });
    }
  }

  clearError() {
    this.error = null;
    this.sessionVerificationError = null;
  }

  /**
   * F-0160 AC-15/16/17. Redeems a sign-up verification token — the link in the confirmation email.
   *
   * ⛔ Does **not** sign anybody in and does not touch stored tokens: this is a fact about the
   * account (`verifiedAt` is now set), not a credential. `VerifyEmailPage` re-fetches `/auth/me`
   * afterwards, only when already signed in, to clear the verification banner in the same tab.
   */
  async confirmEmailVerification(token: string): Promise<boolean> {
    this.loading = true;
    this.error = null;
    try {
      const confirmed = await authApi.confirmEmailVerification(token);
      runInAction(() => {
        this.loading = false;
        this.confirmedEmail = confirmed?.email ?? null;
        if (this.user) this.user = { ...this.user, verifiedAt: new Date().toISOString() };
      });
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        // See `expiredTokenMessage`'s own header: the server's 401 here is a spent/expired link,
        // never a permission problem, and the general mechanism's "(permission)" framing says the
        // wrong thing to a new person who has done nothing wrong.
        this.error =
          expiredTokenMessage(err) ?? this.authErrorMessage(err, 'auth.actions.confirmEmail');
        this.loading = false;
      });
      return false;
    }
  }

  /** F-0160 AC-18. */
  async resendEmailVerification(): Promise<boolean> {
    this.loading = true;
    this.error = null;
    try {
      await authApi.resendEmailVerification();
      runInAction(() => {
        this.loading = false;
      });
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        this.error = this.authErrorMessage(err, 'auth.actions.resendEmail');
        this.loading = false;
      });
      return false;
    }
  }

  /**
   * F-0160 AC-25b: a new confirmation link for somebody the sign-in door just refused with
   * `email_unverified`. They have no session, so the email and password they typed prove the account
   * again; a wrong password is answered as sign-in answers it.
   */
  async resendVerificationForSignIn(email: string, password: string): Promise<boolean> {
    this.verificationResend = 'sending';
    this.verificationResendError = null;
    try {
      const answer = await authApi.resendEmailVerificationForSignIn(email, password);
      runInAction(() => {
        this.verificationResend = answer.sent ? 'sent' : 'already-verified';
      });
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        this.verificationResend = 'failed';
        this.verificationResendError =
          mailRefusalMessage(err) ?? signInFailureMessage(err, 'auth.actions.sendNewLink');
      });
      return false;
    }
  }

  /** Forget a finished sign-up's "check your email", so the form shows again next time. */
  clearPendingVerification() {
    this.pendingVerificationEmail = null;
  }

  /**
   * F-0160 AC-20. The server answers `{ ok: true }` identically whether or not `email` is
   * registered, so a resolved promise here must never be read as "that account exists" — the caller
   * (`ForgotPasswordPage`) shows the same "check your email" copy either way.
   *
   * A *rejected* promise is a different fact: the deployment's mailer is not configured at all
   * (AC-24), which is true for every address alike and is safe to surface, unlike a per-account
   * result would be.
   */
  async requestPasswordReset(email: string): Promise<boolean> {
    this.loading = true;
    this.error = null;
    try {
      await authApi.requestPasswordReset(email);
      runInAction(() => {
        this.loading = false;
      });
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        this.error =
          passwordResetRateLimitSentence(err) ??
          this.authErrorMessage(err, 'auth.actions.requestReset');
        this.loading = false;
      });
      return false;
    }
  }

  /**
   * F-0160 AC-21/22/23. Resolves the review of recently added sign-in methods (T-0633, F-0563 D3),
   * or `null` when the reset failed. A backend from before the review answers with none, which
   * resolves an empty review with `known: false`, so the page claims nothing about what was added.
   */
  async confirmPasswordReset(
    token: string,
    newPassword: string,
  ): Promise<(IPasswordResetReview & { known: boolean }) | null> {
    this.loading = true;
    this.error = null;
    try {
      const answer = await authApi.confirmPasswordReset(token, newPassword);
      runInAction(() => {
        this.loading = false;
      });
      return answer.review
        ? { ...answer.review, known: true }
        : { grant: null, expiresAt: null, recentlyAdded: [], known: false };
    } catch (err: unknown) {
      runInAction(() => {
        // See `expiredTokenMessage`'s own header — the same reasoning as `confirmEmailVerification`.
        this.error =
          passwordResetRateLimitSentence(err) ??
          expiredTokenMessage(err) ??
          this.authErrorMessage(err, 'auth.actions.resetPassword');
        this.loading = false;
      });
      return null;
    }
  }

  /**
   * T-0633 (F-0563 D7): removes one method the reset's review listed. Resolves null on success, or
   * the sentence to show beside that method. Not `this.error`: the failure belongs to one row.
   */
  async removeReviewedSignInMethod(
    grant: string,
    method: Pick<IRecentlyAddedSignInMethod, 'kind' | 'id'>,
  ): Promise<string | null> {
    try {
      await authApi.removeReviewedSignInMethod(grant, method.kind, method.id);
      return null;
    } catch (err: unknown) {
      // The review's 401 is one complete sentence for this person, as the reset's own is (D7).
      const limited = passwordResetRateLimitSentence(err);
      return sayFailure(
        pageWords(),
        limited ??
          expiredTokenMessage(err) ??
          this.authErrorMessage(err, 'auth.actions.removeMethod'),
      );
    }
  }

  /**
   * F-0160 §11: ask whether the deployment is waiting for setup. A failure is silent and leaves the
   * answer unknown — this decides which of two helpful screens to show, and an error box about it
   * on the sign-in page would be noise.
   */
  async loadSetupStatus(): Promise<void> {
    this.setupStatusReach = 'pending';
    try {
      const { required, firstRun } = await authApi.setupStatus();
      runInAction(() => {
        this.setupRequired = required === true;
        this.firstRun = firstRun ?? null;
        this.setupStatusReach = 'answered';
      });
    } catch (error) {
      runInAction(() => {
        this.setupRequired = null;
        this.firstRun = null;
        this.setupStatusReach = isDeploymentUnreachableError(error) ? 'unreachable' : 'answered';
      });
    }
  }

  /**
   * F-0160 §11.5 (I-0638): the browser door. With `passkey`, the ceremony runs first and the
   * account is created only with a proven passkey (AC-72); otherwise with the password.
   */
  async completeFirstRun(
    input: { email: string; name: string } & ({ password: string } | { passkey: true }),
  ): Promise<boolean> {
    return this.signInThroughSetup('auth.actions.finishSetup', async () => {
      if (!('passkey' in input)) {
        return authApi.firstRunSetup({
          email: input.email,
          name: input.name,
          password: input.password,
        });
      }
      const proof = await this.setupPasskey({
        purpose: 'first-run',
        email: input.email,
        name: input.name,
      });
      return authApi.firstRunSetup({ email: input.email, name: input.name, passkey: proof });
    });
  }

  /** F-0160 §11.5: whose account a claim link is for. `null`, with the reason in `error`, if dead. */
  async peekClaim(token: string): Promise<IClaimPeek | null> {
    this.error = null;
    try {
      return await authApi.peekClaim(token);
    } catch (err: unknown) {
      runInAction(() => {
        this.error = setupRefusalMessage(err) ?? failureToSay(err, 'auth.actions.openLink');
      });
      return null;
    }
  }

  /** F-0160 AC-76, AC-77: claim the account an agent set up, with a password or a passkey. */
  async claimAccount(input: { token: string } & ({ password: string } | { passkey: true })) {
    return this.signInThroughSetup('auth.actions.claimAccount', async () => {
      if (!('passkey' in input)) {
        return authApi.claimAccount({ token: input.token, password: input.password });
      }
      const proof = await this.setupPasskey({ purpose: 'claim', token: input.token });
      return authApi.claimAccount({ token: input.token, passkey: proof });
    });
  }

  private async setupPasskey(
    input: Parameters<typeof authApi.setupPasskeyOptions>[0],
  ): Promise<{ challengeId: string; response: RegistrationResponseJSON }> {
    await this.assertWebAuthnUsable();
    const challenge = await authApi.setupPasskeyOptions(input);
    const response = await this.passkeyCredential(challenge.options, challenge.origin);
    return { challengeId: challenge.challengeId, response };
  }

  /** The three setup doors end the same way: a session, and a deployment that is set up. */
  private async signInThroughSetup(
    action: MessageKey,
    obtain: () => Promise<IAuthResponse>,
  ): Promise<boolean> {
    this.loading = true;
    this.error = null;
    try {
      const res = await obtain();
      await storeSignIn(res);
      runInAction(() => {
        this.user = res.user;
        this.setupRequired = false;
        this.firstRun = this.firstRun && { ...this.firstRun, open: false, closesAt: null };
        this.loading = false;
        this.sessionVerificationError = null;
      });
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        this.error =
          setupRefusalMessage(err) ??
          ((err as { name?: string })?.name ? this.passkeyErrorMessage(err, action) : null) ??
          failureToSay(err, action);
        this.loading = false;
      });
      return false;
    }
  }

  /**
   * F-0160 §11 (I-0638): create the first administrator with the operator's one-time token, and
   * sign in as them — the screen they came to reach, Settings → Email, needs a session.
   */
  async completeSetup(input: ICompleteSetupInput): Promise<boolean> {
    this.loading = true;
    this.error = null;
    try {
      const res = await authApi.completeSetup(input);
      await storeSignIn(res);
      runInAction(() => {
        this.user = res.user;
        this.setupRequired = false;
        this.loading = false;
        this.sessionVerificationError = null;
      });
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        this.error = setupRefusalMessage(err) ?? failureToSay(err, 'auth.actions.finishSetup');
        this.loading = false;
      });
      return false;
    }
  }

  private async fetchCurrentUserWithRefresh(): Promise<IUser> {
    if (getAsIfState().view !== null) {
      // A view never locks its starter out of the tab (F-0709, I-2157); its rules are its own chunk.
      const { readPersonUnderView } = await import('@/features/as-if/asIfEnding');
      return readPersonUnderView(
        () => authApi.me(),
        () => this.fetchCurrentUserWithRefresh(),
      );
    }
    try {
      return await authApi.me();
    } catch (err: unknown) {
      if (!isHttpAuthError(err)) throw err;
      if (!holdsCurrentRefreshSecret(REFRESH_KEY)) throw err;
      await refreshAccessToken();
      return authApi.me();
    }
  }

  /**
   * Refuse a passkey ceremony only when this page genuinely cannot run one, and say which of the
   * two reasons it is.
   *
   * We deliberately do not consult `isUserVerifyingPlatformAuthenticatorAvailable()`. That answers
   * "is there a built-in authenticator", which is a preference here, not a requirement: the server
   * asks for `preferredAuthenticatorType: 'localDevice'` without pinning `authenticatorAttachment`,
   * so a security key or a nearby phone completes the ceremony just as well. Gating on it turned a
   * Mac that could enrol a passkey into a Mac that was told its browser could not — which is how
   * this reached us as a defect. The browser's own picker is the honest place to enumerate what is
   * on offer.
   */
  private async assertWebAuthnUsable(): Promise<void> {
    // The desktop window runs no ceremony of its own, so its context decides nothing: the sheet
    // opens the deployment's own page, and `runCeremonyInPasskeySheet` refuses where there is none.
    if (ceremoniesRunInTheSheet()) return;
    if (!window.isSecureContext) {
      throw new ReaderFacingError(
        `Passkeys need a secure connection, and this page is open at ${window.location.origin}. ` +
          'Open FEATit over https, or at http://localhost, and try again.',
      );
    }
    if (!(await webAuthnBrowser()).browserSupportsWebAuthn()) {
      throw new ReaderFacingError(
        'This browser has no passkey support. Sign in with your email and password, or switch to ' +
          'a current version of Safari, Chrome, Edge or Firefox.',
      );
    }
  }

  /**
   * Create a passkey where this page can: here in a browser tab, or in the system's web
   * authentication sheet from the desktop app, whose window WKWebView refuses WebAuthn (I-0775).
   *
   * `origin` is the page the deployment verifies passkeys on. A browser tab on another of the
   * deployment's addresses does not run it (I-1417, `passkeyOrigin.ts`); the desktop opens it, and an
   * older deployment that does not name it is reached at its address.
   */
  private async passkeyCredential(
    options: PublicKeyCredentialCreationOptionsJSON,
    origin: string | undefined,
  ): Promise<RegistrationResponseJSON> {
    if (!ceremoniesRunInTheSheet()) {
      assertCeremonyRunsHere(origin);
      return (await webAuthnBrowser()).startRegistration({ optionsJSON: options });
    }
    return (await loadPasskeySheet()).runCeremonyInPasskeySheet(
      { kind: 'create', options },
      origin ?? effectiveDeploymentAddress(),
    ) as Promise<RegistrationResponseJSON>;
  }

  /** Sign in with a passkey where this page can — see `passkeyCredential`. */
  private async passkeyAssertion(
    options: PublicKeyCredentialRequestOptionsJSON,
    origin: string | undefined,
  ): Promise<AuthenticationResponseJSON> {
    return passkeyAssertion(options, origin);
  }

  /**
   * Turn a failed passkey ceremony into a sentence that names the thing to change. The browser
   * throws a `DOMException` whose `name` is the only reliable discriminator — its `message` is
   * vendor-written and often says nothing (Safari's `NotAllowedError` message is empty).
   */
  private passkeyErrorMessage(err: unknown, action: MessageKey): HeldSentenceType {
    const serverMessage = (err as { response?: { data?: { message?: string } } })?.response?.data
      ?.message;
    if (serverMessage) return serverMessage;

    switch ((err as { name?: string })?.name) {
      case 'PasskeyAtAnotherAddress': {
        // I-1417: this step needs the session this address holds, so it is not moved for the reader.
        const origin = (err as PasskeyAtAnotherAddressError).ceremonyOrigin;
        return `Passkeys for this deployment are used at ${origin}. Open FEATit at ${origin} to ${pageWords()(action)}.`;
      }
      case 'SecurityError':
        /*
         * Two different problems share this name, and they have two different fixes, so
         * SimpleWebAuthn's code is what we branch on rather than the name alone.
         */
        if ((err as { code?: string }).code === 'ERROR_INVALID_DOMAIN') {
          return (
            `Passkeys cannot be issued to ${window.location.hostname}, because it is an IP ` +
            'address rather than a domain name. Open FEATit at its host name, or at ' +
            'http://localhost, and try again.'
          );
        }
        return (
          'This deployment issues passkeys for a different web address than the one you have ' +
          `open (${window.location.origin}). An administrator needs to point the deployment's ` +
          'front-end address at it.'
        );
      case 'ConstraintError':
        /*
         * The one case the old availability gate was reaching for. It belongs here instead: the
         * browser has now actually looked, so this is a finding rather than a prediction.
         */
        return (
          // No sensor by name: this is said on every device, and most have no Touch ID (I-0658).
          'This device cannot verify who you are, and FEATit requires that for a passkey. Set up ' +
          'a fingerprint, face or screen lock, or use a security key or a nearby phone instead.'
        );
      case 'NotAllowedError':
        return 'The passkey prompt was dismissed or timed out. Try again and confirm it.';
      case 'InvalidStateError':
        return 'This device already has a passkey for your account. Use it to sign in.';
      case 'NotSupportedError':
        return 'No authenticator on this device can create the kind of passkey FEATit asks for.';
      case 'AbortError':
        return 'The passkey prompt was interrupted. Try again.';
      default:
        return failureToSay(err, action);
    }
  }

  /**
   * What failed and why — the server's refusal kept, a transport's own text never shown (I-0398).
   *
   * `mailRefusalMessage` first: F-0160 AC-24's `MailNotConfiguredError`/`MailDeliveryFailedError`
   * refusals are 502/503, which `actionFailureText` would otherwise fold into a generic "server
   * error" — correct for an accidental 500, wrong for these two, which are deliberate and
   * administrator-actionable. See that function's header for why this is a narrow exception rather
   * than a change to the general rule.
   */
  private authErrorMessage(err: unknown, action: MessageKey): HeldSentenceType {
    const text = mailRefusalMessage(err) ?? failureToSay(err, action);
    runInAction(() => {
      this.lastRefusal = { text, destination: refusalDestinationOfError(err) };
    });
    return text;
  }

  /** The place the refusal now in `error` named (mail not set up: Global settings → Email). */
  get errorDestination(): RefusalDestinationIdType | null {
    return this.heldError !== null && this.lastRefusal?.text === this.heldError
      ? this.lastRefusal.destination
      : null;
  }
}
